(() => {
  'use strict';
  if (customElements.get('noise-product-discovery')) return;
  const key = 'noise:recent:v1';
  const ttl = 30 * 24 * 60 * 60 * 1000;
  const allowed = () => {
    const privacy = window.Shopify?.customerPrivacy;
    return privacy?.preferencesProcessingAllowed?.() === true && privacy?.analyticsProcessingAllowed?.() === true;
  };
  class NoiseDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.root = this.dataset.root.replace(/\/?$/, '/');
      this.recent = [];
      this.revision = 0;
      this.onConsent = () => this.syncHistory();
      document.addEventListener('visitorConsentCollected', this.onConsent);
      this.querySelectorAll('[data-tab]').forEach((button, index, buttons) => {
        button.addEventListener('click', () => this.select(button.dataset.tab));
        button.addEventListener('keydown', (event) => {
          let target;
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') target = buttons[1 - index];
          if (event.key === 'Home') target = buttons[0];
          if (event.key === 'End') target = buttons[buttons.length - 1];
          if (!target) return;
          event.preventDefault(); target.focus(); this.select(target.dataset.tab);
        });
      });
      this.syncHistory();
      if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
          if (!error && this.isConnected) this.syncHistory();
        });
      }
      // Prepare suggestions before the visitor reaches the section.
      queueMicrotask(() => { if (this.isConnected) this.loadRelated(); });
    }
    disconnectedCallback() {
      document.removeEventListener('visitorConsentCollected', this.onConsent);
      this.observer?.disconnect();
      this.initialized = false;
      this.revision++;
    }
    syncHistory() {
      this.revision++;
      this.recentLoaded = false;
      this.recent = [];
      try {
        if (!allowed()) localStorage.removeItem(key);
        else {
          const saved = JSON.parse(localStorage.getItem(key) || '[]');
          const valid = Array.isArray(saved) ? saved.filter(item => /^[a-z0-9-]+$/i.test(item.handle) && Number.isFinite(item.time) && Date.now() - item.time < ttl && item.time <= Date.now()) : [];
          this.recent = valid.filter(item => item.handle !== this.dataset.handle).slice(0, 12);
          localStorage.setItem(key, JSON.stringify([{ handle: this.dataset.handle, type: this.dataset.productType, time: Date.now() }, ...this.recent].slice(0, 12)));
        }
      } catch { this.recent = []; }
      const panel = this.querySelector('[data-panel="recent"]');
      delete panel.dataset.noiseSignature;
      panel.querySelector('.noise-discovery__rail').replaceChildren();
      panel.querySelector('.noise-discovery__message').hidden = false;
      panel.querySelector('.noise-discovery__message').textContent = allowed() ? this.dataset.empty : this.dataset.consent;
      if (!panel.hidden) this.loadRecent();
      if (this.relatedProducts) this.render('related', this.relatedProducts);
    }
    select(name) {
      this.querySelectorAll('[data-tab]').forEach(button => {
        const active = button.dataset.tab === name;
        button.setAttribute('aria-selected', String(active)); button.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-panel]').forEach(panel => { panel.hidden = panel.dataset.panel !== name; });
      if (name === 'recent') this.loadRecent();
      else this.loadRelated();
    }
    async getJSON(path) {
      const response = await fetch(this.root + path, { signal: AbortSignal.timeout(8000) });
      if (!response.ok) throw new Error('Product request failed');
      return response.json();
    }
    async loadRelated() {
      if (this.relatedStarted) return;
      this.relatedStarted = true;
      try {
        const data = await this.getJSON(`recommendations/products.json?product_id=${encodeURIComponent(this.dataset.productId)}&limit=10&intent=related`);
        if (!this.isConnected) return;
        this.relatedProducts = (data.products || []).filter(item => item.available && String(item.id) !== this.dataset.productId);
        if (this.relatedProducts.length) this.render('related', this.relatedProducts);
      } catch { this.relatedStarted = false; }
    }
    async loadRecent() {
      if (!allowed() || this.recentLoaded || !this.recent.length) return;
      this.recentLoaded = true;
      const revision = this.revision;
      const results = await Promise.allSettled(this.recent.slice(0, 6).map(item => this.getJSON(`products/${encodeURIComponent(item.handle)}.js`)));
      if (!this.isConnected || revision !== this.revision || !allowed()) return;
      const products = results.filter(result => result.status === 'fulfilled' && result.value.available).map(result => result.value);
      this.render('recent', products);
    }
    render(name, products) {
      const panel = this.querySelector(`[data-panel="${name}"]`);
      let sorted = [...products];
      if (name === 'related' && allowed()) {
        const score = item => this.recent.reduce((sum, recent, index) => sum + (recent.type && recent.type === item.type ? 12 - index : 0), 0);
        sorted.sort((a, b) => score(b) - score(a));
      }
      const signature = JSON.stringify(sorted.slice(0, 6).map(p => [p.id, p.title, p.price, p.price_varies, p.featured_image, p.url]));
      if (panel.dataset.noiseSignature === signature) return;
      panel.dataset.noiseSignature = signature;
      const cards = sorted.slice(0, 6).flatMap(product => {
        const href = new URL(product.url || `${this.root}products/${product.handle}`, location.origin);
        if (href.origin !== location.origin || !href.pathname.includes('/products/')) return [];
        const card = document.createElement('a'); card.className = 'noise-discovery__card'; card.href = href.href;
        const image = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.url;
        if (image) {
          const img = document.createElement('img'); img.className = 'noise-discovery__image'; img.alt = product.title; img.loading = 'lazy'; img.width = 240; img.height = 320;
          const url = new URL(image, location.origin); if (url.protocol === 'https:' || url.protocol === 'http:') { url.searchParams.set('width', '480'); img.src = url.href; }
          card.append(img);
        }
        const title = document.createElement('span'); title.className = 'noise-discovery__name'; title.textContent = product.title;
        const price = document.createElement('span'); price.className = 'noise-discovery__price';
        price.textContent = (product.price_varies ? `${this.dataset.from} ` : '') + new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(product.price / 100);
        card.append(title, price); return [card];
      });
      panel.querySelector('.noise-discovery__rail').replaceChildren(...cards);
      const message = panel.querySelector('.noise-discovery__message'); message.hidden = cards.length > 0;
      message.textContent = name === 'recent' ? this.dataset.empty : this.dataset.unavailable;
    }
  }
  customElements.define('noise-product-discovery', NoiseDiscovery);
})();
